/**
 * Step model for the Revenue Attribution setup wizard.
 *
 * Extracted so the (old) vertical wizard and the (new) horizontal wizard share one
 * source of truth for step ordering. The persisted `step` field indexes into these
 * rows, and the page's OAuth return legs resolve their landing step through
 * `stepIndexOf(..., "connect")` — both must agree on the ordering regardless of
 * which wizard renders it.
 */

import { COPY } from "./copy";
import type { SetupPath } from "./setupState";

/**
 * Step rows depend on the route chosen on step 1, so they're built per render
 * rather than hardcoded. Each row carries an id and content is dispatched off
 * that — index arithmetic breaks the moment two paths have different lengths.
 */
export type StepId =
    | "path"
    | "connect"
    | "channel"
    | "events"
    | "next"
    | "comingSoon"
    | "script"
    | "sessionId"
    | "stripe";

export type StepRow = {
    id: StepId;
    /** Short label for the stepper. */
    label: string;
    title: string;
    summary: string;
};

export const PATH_ROW: StepRow = {
    id: "path",
    label: COPY.pathChooser.label,
    title: COPY.pathChooser.heading,
    summary: COPY.pathChooser.subheading
};

export const GA4_ROWS: StepRow[] = [
    {
        id: "connect",
        label: COPY.wizard.steps[0],
        title: COPY.wizard.step1.heading,
        summary: COPY.wizard.step1.summary
    },
    {
        id: "channel",
        label: COPY.wizard.steps[1],
        title: COPY.wizard.step2.heading,
        summary: COPY.wizard.step2.subheading
    },
    {
        id: "events",
        label: COPY.wizard.steps[2],
        title: COPY.wizard.step3.heading,
        summary: COPY.wizard.step3.subheading
    },
    { id: "next", label: COPY.wizard.steps[3], title: COPY.whatsNext.heading, summary: COPY.whatsNext.summary }
];

/** Demo walks the intended Phase 2 flow; live stops at an honest coming-soon. */
export const REVENUE_STACK_DEMO_ROWS: StepRow[] = [
    {
        id: "script",
        label: COPY.revenueStack.script.label,
        title: COPY.revenueStack.script.heading,
        summary: COPY.revenueStack.script.subheading
    },
    {
        id: "sessionId",
        label: COPY.revenueStack.sessionId.label,
        title: COPY.revenueStack.sessionId.heading,
        summary: COPY.revenueStack.sessionId.subheading
    },
    {
        id: "stripe",
        label: COPY.revenueStack.stripe.label,
        title: COPY.revenueStack.stripe.heading,
        summary: COPY.revenueStack.stripe.subheading
    },
    { id: "next", label: COPY.wizard.steps[3], title: COPY.whatsNext.heading, summary: COPY.whatsNext.summary }
];

export const REVENUE_STACK_LIVE_ROWS: StepRow[] = [
    {
        id: "comingSoon",
        label: COPY.revenueStack.comingSoon.label,
        title: COPY.revenueStack.comingSoon.heading,
        summary: COPY.revenueStack.comingSoon.body
    }
];

export function stepRowsFor(path: SetupPath | null, isDemoMode: boolean): StepRow[] {
    if (path === "ga4") return [PATH_ROW, ...GA4_ROWS];
    if (path === "revenue_stack") {
        return [PATH_ROW, ...(isDemoMode ? REVENUE_STACK_DEMO_ROWS : REVENUE_STACK_LIVE_ROWS)];
    }
    return [PATH_ROW];
}

/** 1-based position of a step, so callers never hardcode an index. */
export function stepIndexOf(rows: StepRow[], id: StepId): number {
    const i = rows.findIndex((r) => r.id === id);
    return i === -1 ? 1 : i + 1;
}
